import {
  AdType,
  AD_TYPE,
  CreativeConfig,
  CATALOG_CREATIVE_TYPE,
  CampaignFormData,
  FullNormalFormData,
  AdgroupFormData,
} from '@tt4b/ttam-enums';
import { computed, Ref, ComputedRef, watch } from '@vue/composition-api';
import { i18n } from './../../../i18n';
import { useStdAdgroupIs } from '@tt4b/ttam-hooks';
import { cloneDeep, isEqual } from 'lodash-es';
import { getPermissionLink } from '../utils/index';
import { useTikTokIdentityDisplayProps } from './../../identity/hooks';
export function usePropsIsCollectionAdsTypeVisible(options: {
  isEdit: Ref<boolean>;
  isCollectionAds: Ref<boolean>;
  isCollectionAdsSelectorSupportable: Ref<boolean>;
}) {
  const { isEdit, isCollectionAds, isCollectionAdsSelectorSupportable } = options;

  const isCollectionAdsTypeVisible = computed(() => {
    if (!isCollectionAdsSelectorSupportable.value) {
      return false;
    }
    if (!isEdit.value) {
      return true;
    }

    return isCollectionAds.value;
  });

  return {
    isCollectionAdsTypeVisible,
  };
}

export function usePropsAdTypeList(options: {
  isEdit: Ref<boolean>;
  isNative: Ref<boolean>;
  isLiveAds: Ref<boolean>;
  currentAdType: Ref<AdType>;
  supportAdTypeList: Ref<any[]>;
}) {
  const { isEdit, isNative, currentAdType, supportAdTypeList, isLiveAds } = options;
  const catalogCarouselTypes = [
    AD_TYPE.CATALOGUE_CAROUSEL.value,
    AD_TYPE.CATALOGUE_VIDEO.value,
    AD_TYPE.CATALOGUE_IMAGE.value,
  ];

  const adTypeList = computed(() => {
    if (isEdit.value) {
      return supportAdTypeList.value.filter(item => item.type === currentAdType.value);
    }
    return isNative.value && !isLiveAds.value
      ? supportAdTypeList.value.filter(item => item.type === AD_TYPE.SINGLE_VIDEO.value)
      : supportAdTypeList.value;
  });

  const catalogType = computed(() => adTypeList.value?.filter(curType => catalogCarouselTypes.includes(curType.type)));
  const normalType = computed(() => adTypeList.value?.filter(curType => !catalogCarouselTypes.includes(curType.type)));

  return {
    adTypeList,
    catalogType,
    normalType,
  };
}

export function usePropsDisabled(options: { creativeConfig: Ref<CreativeConfig> }) {
  const { creativeConfig } = options;
  const disabled = computed(() => creativeConfig.value.disableAdformat);

  return {
    disabled,
  };
}

// 处理AdTypeList的一些其他场景
// 计划上的identity，没有授权video ads。创意层级没有授权video ads应该不可选
// 一个adGroup下 single video 和 real time live 只能存在一种
export function useHandlerAdTypeList(options: {
  firstCreativeInfo: Ref<any>;
  adTypeList: ComputedRef<any[]>;
  campaignData: Ref<CampaignFormData>;
  creativeData: Ref<FullNormalFormData>;
  adgroupData: Ref<AdgroupFormData>;
  isCreate: Ref<boolean>;
  isCopy: Ref<boolean>;
  emit?: any;
}) {
  const { firstCreativeInfo, adTypeList, campaignData, creativeData, adgroupData, isCreate, isCopy, emit } = options;
  const { isLiveAds, isProductSales } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const curIdentity = computed(() => ({
    identityId: creativeData?.value?.identityId as string,
    identityTtsShopId: creativeData?.value?.identityTtsShopId as string,
    identityType: creativeData?.value?.identityType as number,
    identityBcId: creativeData?.value?.identityBcId as string,
  }));

  const { canUseVideoList } = useTikTokIdentityDisplayProps({
    identityInfo: curIdentity,
  });
  const handleIdentityNoAccessTiktokPost = ({ item, identityType, identityBcId }) => {
    const url = getPermissionLink({ type: identityType, bcId: identityBcId });
    if (url) {
      item.disabled = true;
      item.disabledTips = i18n.t('module_ad_no_permission_single_video');
      item.disabledTipsLinkInfo = {
        linkText: i18n.t('common_get_permission'),
        linkUrl: url,
      };
    }
  };
  const isProductSalesLSASense = computed(
    () => isProductSales?.value && isLiveAds?.value && (isCreate?.value || isCopy?.value),
  );
  const list = computed(() => {
    const result = cloneDeep(adTypeList?.value);
    const { identityType, identityBcId } = creativeData?.value;
    if (!isProductSalesLSASense.value) {
      return result;
    }
    // 当没有post权限时，默认选real-time。此时如果real-time也被禁用，就不再默认选择。
    for (let i = 0, l = result.length; i < l; i++) {
      const item = result[i];
      if (item.type === AdType.SINGLE_VIDEO && canUseVideoList?.value === false) {
        handleIdentityNoAccessTiktokPost({ item, identityType, identityBcId });
      } else if (
        isCreate?.value &&
        item.type === AdType.SINGLE_VIDEO &&
        firstCreativeInfo.value?.creativeType === CATALOG_CREATIVE_TYPE.SHOP_LIVE_REAL_TIME
      ) {
        item.disabled = true;
        item.disabledTips = i18n.t('module_adgroup_only_contain_single_video');
      } else if (
        isCreate?.value &&
        item.type === AdType.REAL_TIME_LIVE &&
        firstCreativeInfo.value?.creativeType === CATALOG_CREATIVE_TYPE.SHOP_LIVE_VIDEO
      ) {
        item.disabled = true;
        item.disabledTips = i18n.t('module_adgroup_only_contain_real_time_live');
      }
    }
    return result;
  });
  watch(list, (val, oldVal) => {
    if (isEqual(val, oldVal)) {
      return;
    }
    if (val.length || isProductSalesLSASense.value) {
      const disabledItem = val.find(v => v.disabled === true);
      if (!disabledItem) {
        return;
      }
      const item = val.find(v => v.disabled !== true);
      item?.type &&
        emit?.('normal-change', {
          type: item.type,
        });
    }
  });
  return {
    list,
  };
}
